import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Home, Plus, Eye, MessageSquare, TrendingUp, Edit, Trash2, LogOut, Shield, Mail, Phone, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import VetBuyerDialog from "./VetBuyer";
import ServiceRecommendations from "@/components/ServiceRecommendations";
import StatusUpdateDialog from "@/components/StatusUpdateDialog";
import FoundingMemberBadge from "@/components/FoundingMemberBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [showVetDialog, setShowVetDialog] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [selectedListingId, setSelectedListingId] = useState(null);
  const [activeTab, setActiveTab] = useState('listings'); // 'listings' or 'inquiries'
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);

  // Redirect admins to admin dashboard
  useEffect(() => {
    if (user && (user.is_admin || user.is_super_admin)) {
      navigate('/admin');
      return;
    }
    fetchDashboard();
  }, [user]);

  // Handle status update from URL (email link clicks)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const updateStatus = params.get('update_status');
    const status = params.get('status');
    
    if (updateStatus && status && listings.length > 0) {
      const listing = listings.find(l => l.id === updateStatus);
      if (listing) {
        // Auto-submit status update
        axios.post(
          `${API}/listings/${updateStatus}/update-status`,
          { listing_id: updateStatus, status: status },
          { withCredentials: true }
        )
        .then(response => {
          if (response.data.success) {
            toast.success(response.data.message);
            fetchDashboard();
          }
        })
        .catch(error => {
          toast.error("Failed to update status. Please try again.");
          // Still show the dialog for manual update
          setSelectedListing(listing);
          setShowStatusDialog(true);
        });
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [listings]);

  const fetchDashboard = async () => {
    try {
      const [statsRes, listingsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, { withCredentials: true }),
        axios.get(`${API}/listings/my`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setListings(listingsRes.data);
      
      // Fetch inquiries for all listings
      if (listingsRes.data.length > 0) {
        const allInquiries = [];
        for (const listing of listingsRes.data) {
          try {
            const inquiryRes = await axios.get(`${API}/inquiries/listing/${listing.id}`, { withCredentials: true });
            allInquiries.push(...inquiryRes.data.map(inq => ({ ...inq, listing_title: listing.title, listing_id: listing.id })));
          } catch (err) {
            console.error(`Failed to fetch inquiries for listing ${listing.id}`);
          }
        }
        setInquiries(allInquiries);
      }
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };
  
  const handleVetBuyer = (inquiry) => {
    setSelectedBuyer({
      user_id: inquiry.email, // Using email as identifier since we don't have user_id in inquiry
      name: inquiry.name,
      email: inquiry.email,
      phone: inquiry.phone
    });
    setSelectedListingId(inquiry.listing_id);
    setShowVetDialog(true);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      toast.success("Logged out successfully");
      navigate('/');
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  const deleteListing = async (listingId) => {
    if (!window.confirm("Are you sure you want to delete this listing?")) return;
    
    try {
      await axios.delete(`${API}/listings/${listingId}`, { withCredentials: true });
      toast.success("Listing deleted");
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to delete listing");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-slate-50">
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={() => navigate('/professional-services')} variant="ghost" className="text-sm">
                <Sparkles className="w-4 h-4 mr-2" />
                Services
              </Button>
              <Button onClick={() => navigate('/seller-resources')} variant="ghost" className="text-sm">
                <Shield className="w-4 h-4 mr-2" />
                Resources
              </Button>
              <Button onClick={() => navigate('/2fa')} variant="ghost" className="text-sm">
                <Shield className="w-4 h-4 mr-2" />
                {user?.totp_enabled ? '2FA Active' : 'Security'}
              </Button>
              <span className="text-gray-600">Hi, {user?.name}</span>
              <Button onClick={handleLogout} variant="ghost" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>Dashboard</h1>
            <p className="text-gray-600">Manage your For Sale By Owner listings</p>
          </div>
          <Button onClick={() => navigate('/create')} className="bg-orange-600 hover:bg-orange-700" data-testid="create-listing-btn">
            <Plus className="w-5 h-5 mr-2" />
            New Listing
          </Button>
        </div>
        
        {/* Founding Member Badge */}
        {user?.is_founding_member && user?.founding_member_number && (
          <div className="mb-8">
            <FoundingMemberBadge 
              memberNumber={user.founding_member_number}
              size="large"
              showDetails={true}
            />
          </div>
        )}

        {/* Service Recommendations */}
        {listings.length > 0 && listings[0]?.price && (
          <div className="mb-8">
            <ServiceRecommendations 
              stage="active-listing" 
              listingPrice={listings[0].price}
            />
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex space-x-4 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('listings')}
            className={`pb-3 px-2 font-semibold transition-colors ${
              activeTab === 'listings'
                ? 'border-b-2 border-orange-600 text-orange-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid="tab-listings"
          >
            <Home className="w-4 h-4 inline mr-2" />
            My Listings
          </button>
          <button
            onClick={() => setActiveTab('inquiries')}
            className={`pb-3 px-2 font-semibold transition-colors relative ${
              activeTab === 'inquiries'
                ? 'border-b-2 border-orange-600 text-orange-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid="tab-inquiries"
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Buyer Inquiries
            {inquiries.length > 0 && (
              <span className="ml-2 bg-orange-600 text-white text-xs px-2 py-1 rounded-full">
                {inquiries.length}
              </span>
            )}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600"></div>
          </div>
        ) : (
          <>
            {activeTab === 'listings' && (
              <>
            <div className="grid grid-cols-4 gap-6 mb-8">
              <Card className="p-6 border-orange-100">
                <div className="flex items-center justify-between mb-3">
                  <Home className="w-8 h-8 text-orange-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1" data-testid="total-listings">{stats?.total_listings || 0}</div>
                <div className="text-sm text-gray-600">Total Listings</div>
              </Card>
              <Card className="p-6 border-green-100">
                <div className="flex items-center justify-between mb-3">
                  <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1" data-testid="active-listings">{stats?.active_listings || 0}</div>
                <div className="text-sm text-gray-600">Active</div>
              </Card>
              <Card className="p-6 border-blue-100">
                <div className="flex items-center justify-between mb-3">
                  <Eye className="w-8 h-8 text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1" data-testid="total-views">{stats?.total_views || 0}</div>
                <div className="text-sm text-gray-600">Total Views</div>
              </Card>
              <Card className="p-6 border-purple-100">
                <div className="flex items-center justify-between mb-3">
                  <MessageSquare className="w-8 h-8 text-purple-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1" data-testid="total-inquiries">{stats?.total_inquiries || 0}</div>
                <div className="text-sm text-gray-600">Inquiries</div>
              </Card>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-orange-100 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Your Listings</h2>
              </div>
              {listings.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-24 h-24 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center">
                    <Home className="w-12 h-12 text-orange-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>No listings yet</h3>
                  <p className="text-gray-600 mb-6">Create your first listing to get started</p>
                  <Button onClick={() => navigate('/create')} className="bg-orange-600 hover:bg-orange-700" data-testid="create-first-listing-btn">
                    <Plus className="w-5 h-5 mr-2" />
                    Create Listing
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {listings.map(listing => (
                    <div key={listing.id} className="p-6 hover:bg-gray-50 transition-colors" data-testid={`dashboard-listing-${listing.id}`}>
                      <div className="flex items-start space-x-4">
                        <div className="w-32 h-24 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                          {listing.photos && listing.photos.length > 0 ? (
                            <img
                              src={listing.photos[0]}
                              alt={listing.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/400x300/f97316/FFFFFF?text=No+Image';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-orange-100">
                              <Home className="w-8 h-8 text-orange-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="text-xl font-bold text-gray-900 mb-1">{listing.title}</h3>
                              <p className="text-sm text-gray-600">{listing.city}, {listing.county} County</p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-orange-600" style={{ fontFamily: 'Playfair Display' }}>
                                ${listing.price.toLocaleString()}
                              </div>
                              {listing.tier !== 'free' && (
                                <span className="inline-block mt-1 bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs font-semibold">
                                  {listing.tier.toUpperCase()}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-6 text-sm text-gray-600 mb-4">
                            <span>{listing.bedrooms} bed</span>
                            <span>{listing.bathrooms} bath</span>
                            <span>{listing.sqft.toLocaleString()} sqft</span>
                            <span className="flex items-center"><Eye className="w-4 h-4 mr-1" />{listing.views}</span>
                            <span className="flex items-center"><MessageSquare className="w-4 h-4 mr-1" />{listing.inquiries}</span>
                          </div>
                          <div className="flex items-center space-x-3">
                            <Button
                              size="sm"
                              onClick={() => navigate(`/listing/${listing.id}`)}
                              variant="outline"
                              data-testid={`view-listing-${listing.id}`}
                            >
                              View
                            </Button>
                            {listing.status === 'active' && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedListing(listing);
                                  setShowStatusDialog(true);
                                }}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`update-status-${listing.id}`}
                              >
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Update Status
                              </Button>
                            )}
                            <Button
                              size="sm"
                              onClick={() => navigate(`/media-kit/${listing.id}`)}
                              className="bg-purple-600 hover:bg-purple-700 text-white"
                              data-testid={`media-kit-${listing.id}`}
                            >
                              📱 Media Kit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteListing(listing.id)}
                              data-testid={`delete-listing-${listing.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                            {listing.status === 'active' && listing.tier === 'free' && (
                              <Button 
                                size="sm" 
                                onClick={() => navigate(`/upgrade/${listing.id}`)}
                                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0"
                                data-testid={`upgrade-${listing.id}`}
                              >
                                ⭐ Upgrade
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
              </>
            )}
            
            {/* Buyer Inquiries Tab */}
            {activeTab === 'inquiries' && (
              <div className="bg-white rounded-2xl shadow-xl border border-orange-100 overflow-hidden">
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-amber-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Playfair Display' }}>
                        Buyer Inquiries & Vetting
                      </h2>
                      <p className="text-sm text-gray-600">Manage inquiries and run background checks on potential buyers</p>
                    </div>
                    <div className="flex items-center space-x-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                      <Shield className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-semibold text-green-900">Seller Protection Active</span>
                    </div>
                  </div>
                </div>
                
                {inquiries.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="w-24 h-24 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <MessageSquare className="w-12 h-12 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>No Inquiries Yet</h3>
                    <p className="text-gray-600 mb-6">When buyers contact you about your listings, they&apos;ll appear here</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {inquiries.map((inquiry, idx) => (
                      <div key={idx} className="p-6 hover:bg-gray-50 transition-colors" data-testid={`inquiry-${idx}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h3 className="text-lg font-bold text-gray-900">{inquiry.name}</h3>
                                <p className="text-sm text-gray-600 mb-1">
                                  Interested in: <span className="font-semibold text-orange-600">{inquiry.listing_title}</span>
                                </p>
                                <p className="text-xs text-gray-500">
                                  {new Date(inquiry.created_at).toLocaleDateString()} at {new Date(inquiry.created_at).toLocaleTimeString()}
                                </p>
                              </div>
                            </div>
                            
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                              <p className="text-sm text-gray-800 italic">&ldquo;{inquiry.message}&rdquo;</p>
                            </div>
                            
                            <div className="flex items-center space-x-6 text-sm text-gray-600 mb-4">
                              <div className="flex items-center">
                                <Mail className="w-4 h-4 mr-2 text-gray-400" />
                                <span>{inquiry.email}</span>
                              </div>
                              {inquiry.phone && (
                                <div className="flex items-center">
                                  <Phone className="w-4 h-4 mr-2 text-gray-400" />
                                  <span>{inquiry.phone}</span>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex items-center space-x-3">
                              <Button
                                size="sm"
                                onClick={() => window.location.href = `mailto:${inquiry.email}`}
                                variant="outline"
                                data-testid={`email-buyer-${idx}`}
                              >
                                <Mail className="w-4 h-4 mr-2" />
                                Email Buyer
                              </Button>
                              {inquiry.phone && (
                                <Button
                                  size="sm"
                                  onClick={() => window.location.href = `tel:${inquiry.phone}`}
                                  variant="outline"
                                  data-testid={`call-buyer-${idx}`}
                                >
                                  <Phone className="w-4 h-4 mr-2" />
                                  Call
                                </Button>
                              )}
                              <Button
                                size="sm"
                                onClick={() => handleVetBuyer(inquiry)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`vet-buyer-${idx}`}
                              >
                                <Shield className="w-4 h-4 mr-2" />
                                Vet This Buyer
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Buyer Vetting Dialog */}
      {showVetDialog && selectedBuyer && (
        <VetBuyerDialog
          buyerInfo={selectedBuyer}
          listingId={selectedListingId}
          open={showVetDialog}
          onClose={() => {
            setShowVetDialog(false);
            setSelectedBuyer(null);
            setSelectedListingId(null);
          }}
        />
      )}

      {/* Status Update Dialog */}
      <StatusUpdateDialog
        open={showStatusDialog}
        onOpenChange={setShowStatusDialog}
        listing={selectedListing}
        onSuccess={() => {
          fetchDashboard();
          setSelectedListing(null);
        }}
      />
    </div>
  );
};

export default Dashboard;